import cookie from 'js-cookie';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import moment from 'moment';
import PropTypes from 'prop-types';
import { decode } from 'html-entities';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';

import {
  ACCOUNT_URLS,
  APP_ID,
  COOKIES,
  EVENT_ALERTS,
  EVENT_STATUSES,
  KEY_CODES,
  KEY_DOWN,
  PAGE_TITLE,
  RSVP_STATUSES,
  RSVP_TYPES,
  SITE_URL,
} from '../../config';
import { dateToStringShort, timeToString } from '../../utils/dates';
import { EVENT_QUERY, RSVP_MUTATION } from '../../queries/events';
import { GET_ALL_MARKETS_QUERY } from '../../queries/market';
import { initializeApollo } from '../../utils/graphql/useApollo';
import { ME_QUERY } from '../../queries/account';
import { serverSideAuthNoRedirect } from '../../utils/auth/serversideTokens';
import Alert from '../../components/Alert';
import AlertMessage from '../../components/AlertMessage';
import AlertRSVP from '../../components/AlertRSVP';
import BackButton from '../../components/BackButton';
import Button from '../../components/Button';
import Countdown from '../../components/Countdown';
import Details from '../../components/Details';
import escapeHTML from '../../utils/strings/escapeHTML';
import EventHeader from '../../components/EventHeader';
import ImageSet from '../../components/ImageSet';
import Layout from '../../components/Layout';
import Mission from '../../components/Mission';
import PageHeader from '../../components/PageHeader';
import Sponsors from '../../components/Sponsors';
import updateArray from '../../utils/arrays/update';

const LoginModal = dynamic(() => import('../../components/LoginModal'), { ssr: false });

const Event = ({
  alert,
  event,
  date: initialDate,
  dates: initialDates,
  markets,
  marketSlug,
  me,
  mission,
  slug,
  token,
}) => {
  const [dates, setDates] = useState(initialDates);
  const [date, setDate] = useState(initialDate);
  const [alerting, setAlerting] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    description,
    displayCountdown,
    displayLocation,
    displayMission,
    discloseSharedMarketing,
    discloseSharedMarketingLabel,
    market,
    status,
  } = event;
  const { image, isRegistrationClosed, rsvp, rsvps, imageSets, sponsors, locations } = date;

  const title = `${event.name} - ${market.shortName} :: ${PAGE_TITLE}`;
  const cleanDescription = escapeHTML(decode(description.replace(/<(?:.|\n)*?>/gm, '')));

  const { LOGIN } = ACCOUNT_URLS;
  const location = locations[0];
  const cannonicalUrl = `${SITE_URL}events/${marketSlug}/${slug}/${date.date}`;
  const mapsURL = `https://maps.google.com/?q=${location.name}+${location.address1}+${location.city}+${location.state}+${location.postalCode}`; // eslint-disable-line
  const { type: rsvpType } = rsvp;
  const displayRSVP = rsvpType !== RSVP_TYPES.NONE && moment(date.date).isBefore(moment(date.date).add(1, 'day'));

  const onRSVPSubmitted = (response) => {
    setSubmitting(false);

    const { rsvp: newRSVP } = response.rsvp;

    const newDate = { ...date, rsvps: [newRSVP] };
    // console.info('submitted', response, date, dates, newDate);
    setDate(newDate);

    if (dates.length > 1) {
      const newDates = updateArray(dates, dates.indexOf(date), newDate);
      setDates(newDates);
    }

    setAlertType(RSVP_STATUSES[newRSVP.status]);
    setAlerting(true);
  };

  const [upsertRSVP] = useMutation(RSVP_MUTATION, {
    onCompleted: onRSVPSubmitted,
    refetchQueries: ['Event', 'Market'],
  });

  const onDateChange = ({ target: { value } }) => {
    const { history } = window;
    const newDate = dates.find((dat) => dat.value === value);
    history.replaceState({}, '', newDate.url);
    setDate(newDate);
  };

  const onKeyDown = (evt) => {
    if (evt.key === KEY_CODES.ESC) {
      onAlertCancel(); // eslint-disable-line
    }
  };

  const onAlertCancel = () => {
    document.removeEventListener(KEY_DOWN, onKeyDown);
    setAlerting(false);
  };

  const doAlert = (type) => {
    document.addEventListener(KEY_DOWN, onKeyDown, { passive: true });

    setAlertType(type);
    setAlerting(true);
  };

  useEffect(() => {
    if (alert) {
      doAlert(alert);
    }
  }, [alert]);

  const onRSVP = () => {
    const { url } = date.rsvp;

    if (rsvpType === RSVP_TYPES.EXTERNAL && url !== '') {
      // window.open(url);
      return;
    }
    setSubmitting(true);
    upsertRSVP({ variables: { id: date.id } });
  };

  const onRSVPCancel = () => {
    setSubmitting(true);
    upsertRSVP({ variables: { id: date.id, status: RSVP_STATUSES.CANCELLED } });
  };

  useEffect(() => {
    const doRSVP = cookie.get(COOKIES.RSVP);
    if (doRSVP) {
      onRSVP();
    }
    // console.info('remove', COOKIES.RSVP, COOKIES.REDIRECT);
    cookie.remove(COOKIES.RSVP);
  }, []);

  // const onSignIn = () => {
  //   setAlertType(EVENT_ALERTS.SIGN_IN);
  //   setAlerting(true);
  // };

  const onSignInComplete = () => {
    cookie.set(COOKIES.RSVP, '1');
    window.location.reload();
  };

  const getAlert = () => {
    const { BUMMER, OH_NO, SIGN_IN } = EVENT_ALERTS;
    const { CANCELLED, CONFIRMED, WAITLISTED } = RSVP_STATUSES;
    /* eslint-disable jsx-a11y/anchor-is-valid */
    const bummer = [
      <Button route={`/${marketSlug}`} medium>
        Find More Events
      </Button>,
    ];
    switch (alertType) {
      case OH_NO: {
        return <AlertMessage title="Oh No!" subtitle="All spots have been filled." links={bummer} />;
      }
      case BUMMER: {
        return (
          <AlertMessage
            title="RSVP Cancelled"
            subtitle="Thanks for letting us know.<br>We’ll offer your spot to someone else."
            links={bummer}
          />
        );
      }
      case CANCELLED:
        return (
          <AlertMessage
            title="RSVP Cancelled"
            subtitle="Thanks for letting us know.<br>We’ll offer your spot to someone else."
            links={bummer}
          />
        );
      case CONFIRMED:
        return <AlertRSVP date={date} mapsURL={mapsURL} />;
      case WAITLISTED:
        return <AlertRSVP date={date} mapsURL={mapsURL} waitlisted />;
      case SIGN_IN:
        return <LoginModal onComplete={onSignInComplete} />;
      default:
        return false;
    }
  };

  const validRSVP = rsvps.length > 0 && rsvps[0].status !== RSVP_STATUSES.CANCELLED;

  const getButtonProps = () => {
    const { url } = date.rsvp;
    const buttonProps = {
      big: true,
      disabled: submitting,
      onClick: onRSVP,
    };
    let text = 'RSVP';

    if (rsvpType === RSVP_TYPES.EXTERNAL && url !== '') {
      buttonProps.href = url;
      buttonProps.target = '_blank';
      buttonProps.rel = 'noopener noreferrer';
    } else if (submitting) {
      text = '#DoingThings...';
    } else if (!token) {
      buttonProps.href = `${LOGIN}?redirectTo=${cannonicalUrl}`;
      // buttonProps.onClick = onSignIn;
      text = 'Sign in to RSVP';
    } else if (validRSVP) {
      buttonProps.onClick = onRSVPCancel;
      buttonProps.alert = true;
      text = rsvps[0].status === RSVP_STATUSES.CONFIRMED ? 'Cancel Your RSVP' : 'Leave The Waitlist';
    } else if (isRegistrationClosed) {
      buttonProps.gray = true;
      text = 'Join The Waitlist';
    }
    return { buttonProps, text };
  };

  const getButton = () => {
    if (!displayRSVP) {
      return false;
    }

    const { buttonProps, text } = getButtonProps();

    return <Button {...buttonProps}>{text}</Button>;
  };

  /* eslint-disable max-len */
  const getSchemaLocation = () => {
    if (displayLocation) {
      return `{
        "@type":"Place",
        "address":{
          "@type":"PostalAddress",
          "name":"${location.name}",
          "streetAddress":"${location.address1}",
          "addressLocality":"${location.city}",
          "addressRegion":"${location.state}",
          "postalCode":"${location.postalCode}",
          "addressCountry":"United States"
        }
      }`;
    }
    return `{"@type": "VirtualLocation","url": "${cannonicalUrl}"}`;
  };

  const getSchemaStatus = () => {
    switch (status) {
      case EVENT_STATUSES.CANCELLED:
        return 'EventCancelled';
      case EVENT_STATUSES.POSTPONED:
        return 'EventPostponed';
      default:
        return 'EventScheduled';
    }
  };

  const schemaTags = `{
    "@context":"http://schema.org/",
    "@type":"Event",
    "location":${getSchemaLocation()},
    "name":"${escapeHTML(event.name)}",
    "startDate":"${date.timeStartLocal}",
    "description":"${cleanDescription}",
    "image":"${image.src}",
    "eventStatus":"${getSchemaStatus()}"
  }`;

  const button = getButton();
  const adminLinks = [
    {
      params: { id: event.id, step: 'settings' },
      route: 'adminEventEdit',
      title: 'Edit Event',
    },
    {
      params: { id: event.id, step: 'attendees' },
      route: 'adminEventEdit',
      title: 'Event Attendees',
    },
  ];
  const disclosure = `By signing up, you direct Outdoor Voices to share your contact details with ${discloseSharedMarketingLabel}.`;
  return (
    <Layout adminLinks={adminLinks} loggedIn={!!token} markets={markets} market={market} me={me}>
      <Head>
        <title key="title">{title}</title>
        <meta name="description" content={cleanDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={cleanDescription} />
        <meta property="og:image" content={image.src} />
        <meta property="og:image:secure_url" content={image.src} />
        <meta property="og:url" content={cannonicalUrl} />
        <meta property="og:site_name" content="Outdoor Voices" />
        <meta name="apple-itunes-app" content={`app-id=${APP_ID}, app-argument=${cannonicalUrl}`} />
        <meta name="twitter:site" content="@OutdoorVoices" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={cleanDescription} />
        <link rel="canonical" href={cannonicalUrl} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaTags }} />
      </Head>
      <>
        <PageHeader>
          <BackButton route={`/${marketSlug}`}>{market.shortName} Events</BackButton>
        </PageHeader>
        <div id="main-content">
          <EventHeader
            {...event}
            button={button}
            discloseSharedMarketing={discloseSharedMarketing}
            disclosure={disclosure}
            image={image}
          />
        </div>
        <Details
          button={button}
          date={date}
          dates={dates}
          discloseSharedMarketing={discloseSharedMarketing}
          disclosure={disclosure}
          displayLocation={displayLocation}
          mapsURL={mapsURL}
          onDateChange={onDateChange}
          rsvped={validRSVP}
        />
        {displayMission && <Mission {...mission} />}
        {imageSets.length > 0 && imageSets.map((imageSet) => <ImageSet {...imageSet} key={imageSet.id} />)}
        {sponsors.length > 0 && <Sponsors sponsors={sponsors} />}
        {displayCountdown && <Countdown date={date.date} />}
        <Alert cancel={onAlertCancel} up={alerting}>
          {getAlert()}
        </Alert>
      </>
    </Layout>
  );
};

Event.defaultProps = {
  alert: false,
  email: false,
  me: false,
  token: false,
};

Event.propTypes = {
  alert: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  date: PropTypes.shape({
    date: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    gcalUrl: PropTypes.string,
    isRegistrationClosed: PropTypes.bool,
    image: PropTypes.shape(),
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        address1: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        postalCode: PropTypes.string.isRequired,
        state: PropTypes.string.isRequired,
      }),
    ).isRequired,
    rsvp: PropTypes.shape({
      type: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    rsvps: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
      }),
    ).isRequired,
    imageSets: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()])),
    sponsors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    timeStartLocal: PropTypes.string,
  }).isRequired,
  dates: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  email: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  event: PropTypes.PropTypes.shape({
    description: PropTypes.string.isRequired,
    displayCountdown: PropTypes.bool.isRequired,
    displayLocation: PropTypes.bool.isRequired,
    displayMission: PropTypes.bool.isRequired,
    discloseSharedMarketing: PropTypes.bool.isRequired,
    discloseSharedMarketingLabel: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    market: PropTypes.shape({
      shortName: PropTypes.string.isRequired,
    }).isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  markets: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  marketSlug: PropTypes.string.isRequired,
  me: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  mission: PropTypes.shape().isRequired,
  slug: PropTypes.string.isRequired,
  token: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export async function getServerSideProps(context) {
  const { query } = context;

  const { serverAccessToken } = await serverSideAuthNoRedirect(context);
  const apolloClient = initializeApollo({ serverAccessToken });
  let me = null;

  if (serverAccessToken) {
    const { data } = await apolloClient.query({
      query: ME_QUERY,
    });
    me = data.me;
  }

  const { data } = await apolloClient.query({
    query: GET_ALL_MARKETS_QUERY,
  });
  const markets = data.markets.edges.map(({ node }) => node);
  const { alert, date: dateQuery, email, marketSlug, slug, time: timeQuery } = query;
  const response = await apolloClient.query({ query: EVENT_QUERY, variables: { marketSlug, slug } });
  const { event, mission } = response.data;

  if (!event) {
    return {
      // returns the default 404 page with a status code of 404
      notFound: true,
    };
  }
  const image = event.image ? event.image : event.category.image;

  // Build the event dates based on the event and date settings.
  const dates = [];
  event.dates.edges.forEach(({ node }) => {
    const locations = node.locations.length < 1 ? event.locations : node.locations;
    if (locations.length > 1) {
      locations[0].background = '#63da38';
      locations[locations.length - 1].background = '#ff3b30';
    }
    const imageSets = node.imageSets.length < 1 ? event.imageSets : node.imageSets;
    const sponsors = node.sponsors.length < 1 ? event.sponsors : node.sponsors;
    const dateString = dateToStringShort(node);
    const time = timeToString(node);
    const rsvp = {
      type: node.rsvpType,
      url: node.registrationUrl,
    };
    dates.push({
      ...node,
      image,
      label: `${dateString}${time ? `: ${time}${event.displayLocation ? '' : ' CDT'}` : ''}`,
      locations,
      rsvp,
      imageSets,
      sponsors,
      time,
      value: node.id,
    });
  });

  let currentDate = dates[0];
  if (dateQuery) {
    currentDate =
      (timeQuery
        ? dates.find((dat) => dat.date === dateQuery && dat.timeStart === timeQuery)
        : dates.find((dat) => dat.date === dateQuery)) || currentDate;
  }

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      alert: alert || null,
      email: email || null,
      event,
      dates,
      date: currentDate,
      markets,
      me,
      token: serverAccessToken || false,
      marketSlug,
      mission: {
        title: event.missionTitle ? event.missionTitle : mission.title,
        copy: event.missionCopy ? event.missionCopy : mission.copy,
      },
      slug,
    },
  };
}

export default Event;
